import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageSquareText, ChevronDown, ChevronUp, Link2, Zap, Loader2, X, Mail, Sparkles } from 'lucide-react';
import { WhatsAppCatalogView } from './WhatsAppCatalogView';
import { ruleSummary } from './ruleSummary';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/catalog`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export function WhatsAppCatalogSection() {
  const [data, setData] = useState(null);
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [submitting, setSubmitting] = useState('');
  const [linking, setLinking] = useState('');
  const [msg, setMsg] = useState(null);
  const [submitModal, setSubmitModal] = useState(null); // {key, title, newVersion, ruleId}
  const [notify, setNotify] = useState('');
  const [notifySaving, setNotifySaving] = useState(false);
  const [correcting, setCorrecting] = useState('');
  const [correctModal, setCorrectModal] = useState(null); // {key, title, langs:[...], submitting}

  const load = async () => {
    try {
      const r = await axios.get(API, auth());
      setData(r.data);
      return r.data;
    } catch {
      setMsg({ ok: false, text: 'Katalog konnte nicht geladen werden' });
    }
  };

  const loadRules = async () => {
    try {
      const r = await axios.get(`${API}/rules`, auth());
      setRules(r.data?.rules || []);
    } catch { /* Zuordnung optional */ }
  };

  const loadNotify = async () => {
    try {
      const r = await axios.get(`${API}/notify-emails`, auth());
      setNotify((r.data?.emails || []).join(', '));
    } catch { /* optional */ }
  };

  useEffect(() => {
    (async () => {
      const d = await load();
      await loadRules();
      await loadNotify();
      // Live-Status: einmal beim Öffnen frisch von Meta holen (max. 1x/2min)
      if (d?.waba_configured) {
        const last = Number(localStorage.getItem('wa_catalog_sync_ts') || 0);
        if (Date.now() - last > 120000) {
          localStorage.setItem('wa_catalog_sync_ts', String(Date.now()));
          onSync(true);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!submitModal) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) setSubmitModal(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitModal, submitting]);


  const flash = (ok, text) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 4000); };

  // Per-Sprache: direkt einreichen (aktuelle Version), keine Rückfrage
  const onSubmit = async (key, lang) => {
    setSubmitting(lang ? `${key}:${lang}` : `${key}:all`);
    try {
      const r = await axios.post(`${API}/submit`, { key, lang: lang || undefined }, auth());
      flash(!!r.data?.success, r.data?.success ? `Zur Meta-Prüfung eingereicht (${r.data?.meta_name})` : 'Teilweise eingereicht — Details im Status');
      await load();
    } catch (e) {
      flash(false, e?.response?.data?.detail || 'Einreichen fehlgeschlagen');
    } finally {
      setSubmitting('');
    }
  };

  // „Alle einreichen" / „Neue Version": fragt zuerst nach der Zuordnung, falls noch keine gesetzt
  const requestSubmit = (key, newVersion) => {
    const t = (data?.templates || []).find((x) => x.key === key);
    if (t?.linked_rule_id) { doSubmit(key, newVersion, undefined); return; }
    setSubmitModal({ key, title: t?.title || key, newVersion, ruleId: '' });
  };

  const doSubmit = async (key, newVersion, ruleId) => {
    setSubmitting(`${key}:all`);
    try {
      const r = await axios.post(`${API}/submit`,
        { key, new_version: !!newVersion, rule_id: ruleId || undefined }, auth());
      flash(!!r.data?.success,
        r.data?.success
          ? `${newVersion ? 'Neue Version' : 'Vorlage'} eingereicht (${r.data?.meta_name})${ruleId ? ' & zugeordnet' : ''}`
          : 'Teilweise eingereicht — Details im Status');
      await load();
    } catch (e) {
      flash(false, e?.response?.data?.detail || 'Einreichen fehlgeschlagen');
    } finally {
      setSubmitting('');
      setSubmitModal(null);
    }
  };

  const onLink = async (key, ruleId) => {
    setLinking(key);
    try {
      const r = await axios.post(`${API}/link`, { key, rule_id: ruleId || null }, auth());
      flash(true, ruleId
        ? (r.data?.active_links ? 'Zuordnung gespeichert & aktiv' : 'Zuordnung gespeichert — greift nach Meta-Freigabe')
        : 'Zuordnung entfernt');
      await load();
    } catch (e) {
      flash(false, e?.response?.data?.detail || 'Zuordnung fehlgeschlagen');
    } finally {
      setLinking('');
    }
  };

  const onSync = async (silent) => {
    const quiet = silent === true;
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/sync`, {}, auth());
      if (!quiet) flash(true, `Status aktualisiert (${r.data?.synced ?? 0})`);
      await load();
    } catch (e) {
      if (!quiet) flash(false, e?.response?.data?.detail || 'Aktualisieren fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const saveNotify = async () => {
    setNotifySaving(true);
    try {
      const emails = notify.split(/[,;\n]/).map((e) => e.trim()).filter(Boolean);
      const r = await axios.put(`${API}/notify-emails`, { emails }, auth());
      setNotify((r.data?.emails || []).join(', '));
      flash(true, r.data?.emails?.length ? 'Benachrichtigungs-E-Mails gespeichert' : 'Keine gültigen E-Mails — Standard-Empfänger werden verwendet');
    } catch (e) {
      flash(false, e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setNotifySaving(false);
    }
  };

  const onAutocorrect = async (key) => {
    setCorrecting(key);
    try {
      const r = await axios.post(`${API}/autocorrect`, { key }, auth());
      const sug = r.data?.suggestions || {};
      const t = (data?.templates || []).find((x) => x.key === key);
      const langs = Object.entries(sug).map(([lang, v]) => ({
        lang,
        label: v.label || lang,
        reason: v.reason || '',
        rejected: !!v.rejected,
        current: v.current || {},
        draft: {
          header: (v.suggested?.header ?? v.current?.header) || '',
          body: (v.suggested?.body ?? v.current?.body) || '',
          footer: (v.suggested?.footer ?? v.current?.footer) || '',
        },
        hasHeader: v.current?.header != null,
        hasFooter: v.current?.footer != null,
      }));
      setCorrectModal({ key, title: t?.title || key, langs, submitting: false });
    } catch (e) {
      flash(false, e?.response?.data?.detail || 'KI-Korrektur fehlgeschlagen');
    } finally {
      setCorrecting('');
    }
  };

  const updateDraft = (lang, field, value) => {
    setCorrectModal((m) => ({
      ...m,
      langs: m.langs.map((l) => (l.lang === lang ? { ...l, draft: { ...l.draft, [field]: value } } : l)),
    }));
  };

  const applyCorrection = async () => {
    if (!correctModal) return;
    setCorrectModal((m) => ({ ...m, submitting: true }));
    try {
      const corrections = {};
      correctModal.langs.forEach((l) => {
        corrections[l.lang] = {
          body: l.draft.body,
          ...(l.hasHeader ? { header: l.draft.header } : {}),
          ...(l.hasFooter ? { footer: l.draft.footer } : {}),
        };
      });
      const r = await axios.post(`${API}/apply-correction`, { key: correctModal.key, corrections, submit: true }, auth());
      flash(!!r.data?.success, r.data?.success
        ? `Korrigierte Version eingereicht (${r.data?.meta_name})`
        : 'Teilweise eingereicht — Details im Status');
      setCorrectModal(null);
      await load();
    } catch (e) {
      flash(false, e?.response?.data?.detail || 'Einreichen fehlgeschlagen');
      setCorrectModal((m) => (m ? { ...m, submitting: false } : m));
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="wa-catalog-section">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 text-left"
        data-testid="wa-catalog-toggle"
      >
        <div className="flex items-center gap-2">
          <MessageSquareText className="h-5 w-5 text-emerald-600" />
          <div>
            <div className="text-sm font-bold text-gray-900">Standard-Vorlagen (fertig gestaltet)</div>
            <div className="text-xs text-gray-500">Fertige Vorlagen in DE/FR/IT/EN — mit einem Klick bei Meta zur Prüfung einreichen</div>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
      </button>

      {msg && (
        <div className={`mt-3 rounded-lg p-2 text-sm ${msg.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`} data-testid="wa-catalog-msg">
          {msg.text}
        </div>
      )}

      {open && (
        <div className="mt-4">
          <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3" data-testid="wa-catalog-notify">
            <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-slate-700">
              <Mail className="h-4 w-4 text-emerald-600" /> Benachrichtigungs-E-Mails bei Freigabe
            </div>
            <p className="mb-2 text-[11px] text-slate-500">
              Empfänger für Freigabe-Meldungen (falls kein Push-Gerät registriert ist). Mehrere Adressen mit Komma trennen. Leer = aktive Admins.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                value={notify}
                onChange={(e) => setNotify(e.target.value)}
                placeholder="z. B. info@strongfit.ch, chef@strongfit.ch"
                data-testid="wa-catalog-notify-input"
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-emerald-400 focus:outline-none"
              />
              <button
                onClick={saveNotify}
                disabled={notifySaving}
                data-testid="wa-catalog-notify-save"
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {notifySaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Speichern
              </button>
            </div>
          </div>
          <WhatsAppCatalogView
            data={data}
            rules={rules}
            onSubmit={onSubmit}
            onSubmitAll={(key) => requestSubmit(key, false)}
            onSubmitNewVersion={(key) => requestSubmit(key, true)}
            onAutocorrect={onAutocorrect}
            onSync={onSync}
            onLink={onLink}
            syncing={syncing}
            submitting={submitting}
            correcting={correcting}
            linking={linking}
            configHint="Business-Account-ID & Access-Token findest du in deinen WhatsApp-Einstellungen."
          />
        </div>
      )}

      {submitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="wa-catalog-submit-modal" onClick={() => !submitting && setSubmitModal(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                <Link2 className="h-4 w-4 text-indigo-600" />
                Zu welcher Automatisierung gehört „{submitModal.title}"?
              </div>
              <button onClick={() => setSubmitModal(null)} data-testid="wa-catalog-submit-modal-close" className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mb-3 text-xs text-slate-500">
              {submitModal.newVersion ? 'Die Vorlage wird als neue Version bei Meta eingereicht.' : 'Die Vorlage wird bei Meta zur Prüfung eingereicht.'}
              {' '}Nach der Freigabe wird sie automatisch dieser Automatisierung zugeordnet und der Versand aktiviert.
            </p>
            <select
              value={submitModal.ruleId}
              onChange={(e) => setSubmitModal((m) => ({ ...m, ruleId: e.target.value }))}
              data-testid="wa-catalog-submit-modal-select"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-indigo-400 focus:outline-none"
            >
              <option value="">— Automatisierung wählen —</option>
              {rules.map((r) => (<option key={r.id} value={r.id}>{r.name || r.id}</option>))}
            </select>
            {submitModal.ruleId && rules.find((r) => r.id === submitModal.ruleId) && (
              <div className="mt-2 flex items-center gap-1 text-[11px] text-indigo-600" data-testid="wa-catalog-submit-modal-summary">
                <Zap className="h-3 w-3" /> Löst aus: {ruleSummary(rules.find((r) => r.id === submitModal.ruleId))}
              </div>
            )}
            <div className="mt-4 flex flex-col gap-2 sm:flex-row-reverse">
              <button
                onClick={() => doSubmit(submitModal.key, submitModal.newVersion, submitModal.ruleId)}
                disabled={!submitModal.ruleId || !!submitting}
                data-testid="wa-catalog-submit-modal-confirm"
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Einreichen & zuordnen
              </button>
              <button
                onClick={() => doSubmit(submitModal.key, submitModal.newVersion, undefined)}
                disabled={!!submitting}
                data-testid="wa-catalog-submit-modal-skip"
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Ohne Zuordnung einreichen
              </button>
            </div>
          </div>
        </div>
      )}

      {correctModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="wa-catalog-correct-modal" onClick={() => !correctModal.submitting && setCorrectModal(null)}>
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-2 border-b border-slate-100 p-5 pb-3">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                <Sparkles className="h-4 w-4 text-violet-600" />
                KI-Korrekturvorschlag — „{correctModal.title}"
              </div>
              <button onClick={() => setCorrectModal(null)} data-testid="wa-catalog-correct-modal-close" className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-5">
              <p className="text-xs text-slate-500">
                Vorschlag auf Basis des Meta-Ablehnungsgrunds. Du kannst den Text noch anpassen. Platzhalter wie <code className="rounded bg-slate-100 px-1">{'{{1}}'}</code> bitte beibehalten. Beim Einreichen wird eine neue Version (v+1) erstellt.
              </p>
              {correctModal.langs.map((l) => (
                <div key={l.lang} className="rounded-xl border border-slate-200 p-3" data-testid={`wa-catalog-correct-lang-${l.lang}`}>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">{l.label}</span>
                    {l.rejected
                      ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">abgelehnt</span>
                      : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">mitkorrigiert</span>}
                  </div>
                  {l.reason && (
                    <div className="mb-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] text-red-700">
                      <span className="font-semibold">Ablehnungsgrund:</span> {l.reason}
                    </div>
                  )}
                  {l.hasHeader && (
                    <label className="mb-2 block">
                      <span className="mb-0.5 block text-[11px] font-medium text-slate-500">Titel</span>
                      <input
                        type="text"
                        value={l.draft.header}
                        onChange={(e) => updateDraft(l.lang, 'header', e.target.value)}
                        data-testid={`wa-catalog-correct-header-${l.lang}`}
                        className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm text-slate-700 focus:border-violet-400 focus:outline-none"
                      />
                    </label>
                  )}
                  <label className="mb-2 block">
                    <span className="mb-0.5 block text-[11px] font-medium text-slate-500">Text</span>
                    <textarea
                      value={l.draft.body}
                      onChange={(e) => updateDraft(l.lang, 'body', e.target.value)}
                      rows={3}
                      data-testid={`wa-catalog-correct-body-${l.lang}`}
                      className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm text-slate-700 focus:border-violet-400 focus:outline-none"
                    />
                  </label>
                  {l.hasFooter && (
                    <label className="block">
                      <span className="mb-0.5 block text-[11px] font-medium text-slate-500">Fusszeile</span>
                      <input
                        type="text"
                        value={l.draft.footer}
                        onChange={(e) => updateDraft(l.lang, 'footer', e.target.value)}
                        data-testid={`wa-catalog-correct-footer-${l.lang}`}
                        className="w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm text-slate-700 focus:border-violet-400 focus:outline-none"
                      />
                    </label>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-2 border-t border-slate-100 p-5 pt-3 sm:flex-row-reverse">
              <button
                onClick={applyCorrection}
                disabled={correctModal.submitting}
                data-testid="wa-catalog-correct-submit"
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {correctModal.submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Als neue Version einreichen
              </button>
              <button
                onClick={() => setCorrectModal(null)}
                disabled={correctModal.submitting}
                data-testid="wa-catalog-correct-cancel"
                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
